import { stringToBytes } from "uuid/dist/v35";
import { LoginCredentials, OtpVerification, OtpLoginCodeVerification, LoginResponse } from "../model/authentication";
import { apiSlice } from "../stores/base/api-slice";
import { resolveUtilityName } from "../tenant/branding/resolveTenant";

const authServiceBaseUrl = "/user/auth";
const authEndpoints = apiSlice.injectEndpoints({
  endpoints: builder => ({
    login: builder.mutation<LoginResponse, LoginCredentials>({
      query: credentials => ({
        url: "/user/auth/login",
        method: "POST",
        // utilityName tells the backend which tenant is signing in (derived from host/URL).
        body: { ...credentials, utilityName: resolveUtilityName() },
      }),
    }),
    // Local-credential login for utility admins on external-auth tenants (AEDC), whose
    // /login goes to Self-Care, where admins have no account.
    xeppLogin: builder.mutation<LoginResponse, LoginCredentials>({
      query: credentials => ({
        url: "/user/auth/xepp-login",
        method: "POST",
        body: { ...credentials, utilityName: resolveUtilityName() },
      }),
    }),
    logout:builder.mutation<void, void>({
      query: credentials => ({
        url: "/user/auth/logout",
        method: "POST",
      }),
    }),
    forgotPassword: builder.mutation<any, string>({
      query: email => ({
        url: `${authServiceBaseUrl}/initiate-reset-password`,
        method: "POST",
        body: { email, utilityName: resolveUtilityName() },
      }),
    }),
    resetPassword: builder.mutation<any, { token: string; password: string; email: string }>({
      query: ({ token, password, email }) => ({
        url: `${authServiceBaseUrl}/finalize-reset-password/${token}`,
        method: "POST",
        body: { email, password, utilityName: resolveUtilityName() },
      }),
    }),
    // Switched from a GET query to a POST mutation so the email can travel in the body
    // (avoids leaking it via URL/query strings, and matches backend rate-limit lookup-by-email).
    validateResetPasswordCode: builder.mutation<any, { token: string; email: string }>({
      query: ({ token, email }) => ({
        url: `${authServiceBaseUrl}/validate-reset-password-code/${token}`,
        method: "POST",
        body: { email, utilityName: resolveUtilityName() },
      }),
    }),
    resendOtp: builder.mutation<any, string>({
      query: email => ({
        url: "/authentication/resend-otp",
        method: "POST",
        body: { email, utilityName: resolveUtilityName() },
      }),
    }),
    verifyOtp: builder.mutation<any, OtpVerification>({
      query: credentials => ({
        url: "/authentication/verify-otp",
        method: "POST",
        body: { ...credentials, utilityName: resolveUtilityName() },
      }),
    }),

    validateOtpLoginCode: builder.mutation<LoginResponse, OtpLoginCodeVerification>({
      query: credentials => ({
        url: "/user/auth/validate-otp-login-code",
        method: "POST",
        body: { ...credentials, utilityName: resolveUtilityName() },
      }),
    }),

    resendOtpLoginCode: builder.mutation<any, { email: string }>({
      query: ({ email }) => ({
        url: "/user/auth/resend-otp-login-code",
        method: "POST",
        body: { email, utilityName: resolveUtilityName() },
      }),
    }),

    refreshToken: builder.mutation<any, string>({
      query: refreshToken => ({
        url: "/user/auth/refresh",
        method: "POST",
        body: { refreshToken, utilityName: resolveUtilityName() },
      }),
    }),
    requestPasscode: builder.mutation<{ userId: string}, { identifier: string }>({
      query: body => ({
        url: "/user/auth/passcode/request",
        method: "POST",
        body: { ...body, utilityName: resolveUtilityName() },
      }),
    }),
    verifyPasscode: builder.mutation<LoginResponse, { userId: string, passcode: string }>({
      query: body => ({
        url: "/user/auth/passcode/verify",
        method: "POST",
        body: { ...body, utilityName: resolveUtilityName() },
      }),
    })
  }),
});

export const {
  useLoginMutation,
  useXeppLoginMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useResendOtpMutation,
  useVerifyOtpMutation,
  useValidateOtpLoginCodeMutation,
  useResendOtpLoginCodeMutation,
  useValidateResetPasswordCodeMutation,
  useRefreshTokenMutation,
  useLogoutMutation,
  useRequestPasscodeMutation,
  useVerifyPasscodeMutation,
} = authEndpoints;